<template>
  <div class="companies-container">
    <div class="container pt-4 pb-5">
      <div class="d-flex justify-content-between align-items-center mb-4">
        <h1 class="mb-0">Firmen</h1>
        <router-link to="/companies/create" class="btn btn-primary">
          <i class="fas fa-plus me-2"></i>Neue Firma
        </router-link>
      </div>
      
      <div class="card shadow-sm">
        <div class="card-body p-0">
          <!-- Filter-Bereich -->
          <div class="p-3 border-bottom d-flex justify-content-between align-items-center">
            <div class="search-box">
              <div class="input-group">
                <span class="input-group-text bg-light border-end-0">
                  <i class="fas fa-search text-muted"></i>
                </span>
                <input 
                  type="text" 
                  class="form-control border-start-0 bg-light" 
                  placeholder="Firma suchen..." 
                  v-model="searchQuery"
                  @input="filterCompanies"
                />
              </div>
            </div>
            
            <!-- Aktionsmenü für Massenaktionen -->
            <div class="d-flex">
              <button 
                class="btn btn-outline-secondary dropdown-toggle" 
                type="button" 
                id="actionsDropdown" 
                data-bs-toggle="dropdown" 
                aria-expanded="false"
                :disabled="!hasSelectedCompanies"
              >
                Aktionen
              </button>
              <ul class="dropdown-menu dropdown-menu-end" aria-labelledby="actionsDropdown">
                <li><a class="dropdown-item" href="#" @click.prevent="impersonateSelected" v-if="selectedCompanies.length === 1"><i class="fas fa-user-secret me-2"></i>Als diese Firma anmelden</a></li>
                <li><a class="dropdown-item" href="#" @click.prevent="confirmDeleteSelected"><i class="fas fa-trash-alt me-2 text-danger"></i>Ausgewählte löschen</a></li>
                <li><a class="dropdown-item" href="#" @click.prevent="exportSelected"><i class="fas fa-file-export me-2"></i>Exportieren</a></li>
              </ul>
            </div>
          </div>
          
          <!-- Ladeanzeige -->
          <div v-if="loading" class="text-center py-5">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Lädt...</span>
            </div>
            <p class="mt-2">Daten werden geladen...</p>
          </div>
          
          <!-- Fehlermeldung -->
          <div v-else-if="error" class="alert alert-danger m-3">
            {{ error }}
          </div>
          
          <!-- Keine Daten -->
          <div v-else-if="filteredCompanies.length === 0" class="text-center py-5">
            <i class="fas fa-building fa-3x text-muted mb-3"></i>
            <h3>Keine Firmen gefunden</h3>
            <p class="text-muted">{{ searchQuery ? 'Keine Ergebnisse für Ihre Suche.' : 'Fügen Sie Ihre erste Firma hinzu!' }}</p>
            <router-link to="/companies/create" class="btn btn-primary mt-2" v-if="!searchQuery">
              <i class="fas fa-plus me-2"></i>Neue Firma
            </router-link>
          </div>
          
          <!-- Firmenliste -->
          <div v-else class="table-responsive">
            <table class="table table-hover mb-0">
              <thead class="table-light">
                <tr>
                  <th scope="col" style="width: 40px;">
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        id="selectAll" 
                        v-model="selectAll"
                        @change="toggleSelectAll"
                      />
                    </div>
                  </th>
                  <th scope="col" style="width: 70px;">Logo</th>
                  <th scope="col">Name</th>
                  <th scope="col">E-Mail</th>
                  <th scope="col">Telefon</th>
                  <th scope="col">Website</th>
                  <th scope="col" style="width: 120px;">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="company in filteredCompanies" :key="company.id">
                  <td>
                    <div class="form-check">
                      <input 
                        class="form-check-input" 
                        type="checkbox" 
                        :id="`company-${company.id}`" 
                        v-model="selectedCompanies"
                        :value="company.id"
                      />
                    </div>
                  </td>
                  <td>
                    <div class="company-logo">
                      <img v-if="company.logo_url" :src="company.logo_url" :alt="company.name" class="img-thumbnail" />
                      <div v-else class="logo-placeholder">
                        <span>{{ getInitials(company.name) }}</span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <router-link :to="`/companies/${company.id}/edit`" class="fw-semibold text-decoration-none">
                      {{ company.name }}
                    </router-link>
                  </td>
                  <td>{{ company.email }}</td>
                  <td>{{ company.phone || '-' }}</td>
                  <td>
                    <a v-if="company.website" :href="company.website" target="_blank" class="text-decoration-none">
                      {{ formatWebsite(company.website) }}
                      <i class="fas fa-external-link-alt fa-xs ms-1"></i>
                    </a>
                    <span v-else>-</span>
                  </td>
                  <td>
                    <div class="d-flex">
                      <button class="btn btn-sm btn-outline-secondary me-2" @click="impersonateCompany(company)" title="Als diese Firma anmelden">
                        <i class="fas fa-user-secret"></i>
                      </button>
                      <router-link :to="`/companies/${company.id}/edit`" class="btn btn-sm btn-outline-primary me-2" title="Bearbeiten">
                        <i class="fas fa-edit"></i>
                      </router-link>
                      <div v-if="company.deleteConfirmation" class="delete-confirmation d-flex align-items-center">
                        <span class="confirmation-text me-2 small">Sicher?</span>
                        <button class="btn btn-sm btn-success me-1" @click="directConfirmDelete(company)">
                          <i class="fas fa-check"></i>
                        </button>
                        <button class="btn btn-sm btn-secondary" @click="cancelDirectDelete(company)">
                          <i class="fas fa-times"></i>
                        </button>
                      </div>
                      <button v-else class="btn btn-sm btn-outline-danger" @click="showDirectDelete(company)" title="Löschen">
                        <i class="fas fa-trash-alt"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Löschbestätigung Modal -->
    <div class="modal fade" :class="{ show: showDeleteModal }" tabindex="-1" style="display: block;" v-if="showDeleteModal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ isMultiDelete ? 'Firmen löschen' : 'Firma löschen' }}</h5>
            <button type="button" class="btn-close" @click="showDeleteModal = false"></button>
          </div>
          <div class="modal-body">
            <p v-if="isMultiDelete">
              Sind Sie sicher, dass Sie <strong>{{ selectedCompanies.length }} Firmen</strong> löschen möchten?
            </p>
            <p v-else>
              Sind Sie sicher, dass Sie die Firma <strong>{{ companyToDelete.name }}</strong> löschen möchten?
            </p>
            <p class="text-danger">
              Diese Aktion kann nicht rückgängig gemacht werden und alle zugehörigen Daten gehen verloren.
            </p>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showDeleteModal = false">Abbrechen</button>
            <button type="button" class="btn btn-danger" @click="deleteCompanies" :disabled="deleting">
              <span class="spinner-border spinner-border-sm me-2" v-if="deleting"></span>
              <i class="fas fa-trash-alt me-2" v-else></i>Löschen bestätigen
            </button>
          </div>
        </div>
      </div>
      <div class="modal-backdrop fade show"></div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useCompanyStore } from '@/stores/company';
import { useCompaniesStore } from '@/stores/companies';
import { useAuthStore } from '@/stores/auth';
import { useToast } from 'vue-toastification';
import http from '@/services/http';
import { API_BASE_URL } from '@/config/api';

export default defineComponent({
  name: 'CompaniesList',
  setup() {
    const router = useRouter();
    const companyStore = useCompanyStore();
    const companiesStore = useCompaniesStore();
    const authStore = useAuthStore();
    const toast = useToast();
    
    // Reaktive Zustände
    const searchQuery = ref('');
    const error = ref(null);
    const loading = ref(false);
    const selectAll = ref(false);
    const selectedCompanies = ref([]);
    const deleting = ref(false);
    const showDeleteModal = ref(false);
    const companyToDelete = ref(null);
    const companies = ref([]);
    const filteredCompanies = ref([]);
    
    // Computed Properties
    const hasSelectedCompanies = computed(() => {
      return selectedCompanies.value.length > 0;
    });
    
    const isMultiDelete = computed(() => {
      return selectedCompanies.value.length > 1;
    });
    
    const company = computed(() => companyStore.currentCompany);
    const user = computed(() => authStore.getUser);
    const isSuperAdmin = computed(() => authStore.isSuperAdmin);
    const isImpersonating = computed(() => authStore.isImpersonating);
    
    // Methoden
    const fetchCompaniesData = async () => {
      console.log('Starte Laden der Firmen...');
      loading.value = true;
      error.value = null;
      
      try {
        // Firmen abrufen
        const result = await companiesStore.fetchCompanies();
        console.log('Firmen vom Store geladen:', result);
        
        // Sicherstellen, dass es ein Array ist
        if (Array.isArray(result)) {
          companies.value = result;
          console.log('Firmen erfolgreich geladen:', companies.value);
        } else {
          console.warn('Keine Firmen gefunden oder kein Array zurückgegeben');
          companies.value = [];
        }
        
        // Gefilterte Firmen aktualisieren
        filterCompanies();
      } catch (err) {
        console.error('Fehler beim Laden der Firmen:', err);
        error.value = 'Fehler beim Laden der Firmendaten. Bitte versuchen Sie es später erneut.';
        companies.value = [];
        filteredCompanies.value = [];
      } finally {
        loading.value = false;
      }
    };
    
    const filterCompanies = () => {
      // Falls keine Firmen vorhanden sind, leere Liste zurückgeben
      if (!companies.value || !Array.isArray(companies.value) || companies.value.length === 0) {
        filteredCompanies.value = [];
        return;
      }
      
      if (!searchQuery.value) {
        filteredCompanies.value = [...companies.value];
        return;
      }
      
      const query = searchQuery.value.toLowerCase();
      filteredCompanies.value = companies.value.filter(company => {
        // Zuerst sicherstellen, dass company ein gültiges Objekt ist
        if (!company) return false;
        
        return (
          (company.name && company.name.toLowerCase().includes(query)) ||
          (company.email && company.email.toLowerCase().includes(query)) ||
          (company.phone && company.phone.toLowerCase().includes(query)) ||
          (company.website && company.website.toLowerCase().includes(query))
        );
      });
    };
    
    const toggleSelectAll = () => {
      if (selectAll.value) {
        selectedCompanies.value = filteredCompanies.value.map(company => company.id);
      } else {
        selectedCompanies.value = [];
      }
    };
    
    const confirmDelete = (company) => {
      companyToDelete.value = company;
      selectedCompanies.value = [company.id];
      showDeleteModal.value = true;
    };
    
    const confirmDeleteSelected = () => {
      companyToDelete.value = null;
      showDeleteModal.value = true;
    };
    
    const deleteCompanies = async () => {
      deleting.value = true;
      
      try {
        // Für jede ausgewählte Firma eine Löschanfrage senden
        const deletePromises = selectedCompanies.value.map(id => 
          companiesStore.deleteCompany(id)
        );
        
        await Promise.all(deletePromises);
        
        toast.success(`${isMultiDelete.value ? 'Firmen wurden' : 'Firma wurde'} erfolgreich gelöscht!`);
        showDeleteModal.value = false;
        selectedCompanies.value = [];
        selectAll.value = false;
        
        // Daten neu laden
        await fetchCompaniesData();
      } catch (err) {
        error.value = 'Fehler beim Löschen der Firma(en). Bitte versuchen Sie es später erneut.';
        console.error('Fehler beim Löschen der Firma(en):', err);
      } finally {
        deleting.value = false;
      }
    };
    
    // Methoden für die direkte Löschfunktion ohne Modal
    const showDirectDelete = (company) => {
      company.deleteConfirmation = true;
    };
    
    const directConfirmDelete = async (company) => {
      if (!company) return;
      
      try {
        await companiesStore.deleteCompany(company.id);
        
        toast.success(`Firma "${company.name}" wurde erfolgreich gelöscht!`);
        
        // Daten neu laden
        await fetchCompaniesData();
      } catch (err) {
        error.value = 'Fehler beim Löschen der Firma. Bitte versuchen Sie es später erneut.';
        console.error('Fehler beim Löschen der Firma:', err);
      } finally {
        company.deleteConfirmation = false;
      }
    };
    
    const cancelDirectDelete = (company) => {
      company.deleteConfirmation = false;
    };
    
    const exportSelected = () => {
      // Implementation für den Export ausgewählter Firmen
      toast.info('Export-Funktion noch nicht implementiert.');
    };
    
    const impersonateSelected = () => {
      if (selectedCompanies.value.length !== 1) return;
      
      const companyId = selectedCompanies.value[0];
      const company = companies.value.find(c => c.id === companyId);
      
      if (!company) return;
      
      // Implementierung für das Wechseln zur ausgewählten Firma
      toast.info(`Wechsle zu Firma: ${company.name}`);
    };
    
    const viewCompany = (id) => {
      router.push({ name: 'CompanyDetail', params: { id } });
    };
    
    const editCompany = (id) => {
      router.push({ name: 'CompanyEdit', params: { id } });
    };
    
    const getInitials = (name) => {
      if (!name) return '?'; // Fallback, wenn kein Name vorhanden ist
      
      return name
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .substring(0, 2)
        .toUpperCase();
    };
    
    const formatWebsite = (url) => {
      if (!url) return '';
      // Entfernt "http://" oder "https://" und "www." für die Anzeige
      return url.replace(/^https?:\/\//, '').replace(/^www\./, '');
    };
    
    // Debug-Methoden
    const debugFetchCompanies = async () => {
      try {
        toast.info('Direkter API-Test gestartet...');
        
        console.log('Verwende API-Basis-URL:', API_BASE_URL);
        
        // Direkte Fetch-Anfrage an beide möglichen Endpunkte
        try {
          const response1 = await fetch(`${API_BASE_URL}/api/companies`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authStore.token || ''}`
            }
          });
          console.log('Direkter API-Test /api/companies:', {
            status: response1.status,
            statusText: response1.statusText
          });
          if (response1.ok) {
            const data1 = await response1.json();
            console.log('Daten von /api/companies:', data1);
          }
        } catch (err1) {
          console.error('Fehler bei direktem API-Test /api/companies:', err1);
        }
        
        try {
          const response2 = await fetch(`${API_BASE_URL}/api/company`, {
            headers: {
              'Accept': 'application/json',
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authStore.token || ''}`
            }
          });
          console.log('Direkter API-Test /api/company:', {
            status: response2.status,
            statusText: response2.statusText
          });
          if (response2.ok) {
            const data2 = await response2.json();
            console.log('Daten von /api/company:', data2);
          }
        } catch (err2) {
          console.error('Fehler bei direktem API-Test /api/company:', err2);
        }
        
        // Aktuellen Store-Zustand ausgeben
        console.log('Aktueller Store-Zustand:', {
          companies: companiesStore.companies,
          loading: companiesStore.loading,
          error: companiesStore.error,
          lastApiResponse: companiesStore.lastApiResponse
        });
        
        toast.success('API-Tests abgeschlossen. Siehe Konsole für Details.');
      } catch (err) {
        console.error('Fehler beim API-Test:', err);
        toast.error('API-Test fehlgeschlagen. Siehe Konsole für Details.');
      }
    };
    
    // Impersonation (Als-Firma-anmelden) Funktion
    const impersonateCompany = async (company) => {
      try {
        loading.value = true;
        
        // Debug-Log hinzufügen
        console.log("Starte Impersonierung für Firma:", company);
        
        // Impersonierung starten
        const result = await authStore.impersonateUser(company.id);
        
        // Debug-Log nach erfolgreicher Impersonierung
        console.log("Impersonierung erfolgreich, Ergebnis:", result);
        console.log("Firma in authStore:", authStore.company);
        
        // Die Firmendaten explizit im Store setzen
        authStore.setCompany(company);
        
        // Nach erfolgreicher Impersonierung zur Firmenseite weiterleiten
        toast.success(`Sie sind jetzt als ${company.name} angemeldet`);
        router.push('/company/dashboard');
      } catch (error) {
        console.error('Fehler bei der Impersonierung:', error);
        toast.error('Fehler bei der Impersonierung: ' + (error.response?.data?.message || error.message));
      } finally {
        loading.value = false;
      }
    };
    
    // Lifecycle Hooks
    onMounted(() => {
      fetchCompaniesData();
    });
    
    return {
      // State
      searchQuery,
      error,
      loading,
      selectAll,
      selectedCompanies,
      deleting,
      showDeleteModal,
      companyToDelete,
      companies,
      filteredCompanies,
      
      // Computed
      hasSelectedCompanies,
      isMultiDelete,
      company,
      user,
      isSuperAdmin,
      isImpersonating,
      
      // Methods
      fetchCompaniesData,
      filterCompanies,
      toggleSelectAll,
      confirmDelete,
      confirmDeleteSelected,
      deleteCompanies,
      exportSelected,
      impersonateSelected,
      viewCompany,
      editCompany,
      getInitials,
      formatWebsite,
      
      // Debug-Methoden
      debugFetchCompanies,
      impersonateCompany,
      
      // Methoden für die direkte Löschfunktion ohne Modal
      showDirectDelete,
      directConfirmDelete,
      cancelDirectDelete
    };
  }
});
</script>

<style lang="scss" scoped>
.companies-container {
  .card {
    border-radius: 10px;
    border: none;
  }
  
  .search-box {
    max-width: 400px;
    width: 100%;
  }
  
  .company-logo {
    width: 40px;
    height: 40px;
    overflow: hidden;
    border-radius: 6px;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
    
    .logo-placeholder {
      width: 100%;
      height: 100%;
      background-color: var(--primary-color);
      color: white;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      font-size: 14px;
    }
  }
  
  .btn-primary {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
    
    &:hover {
      background-color: var(--primary-color-dark);
      border-color: var(--primary-color-dark);
    }
  }
  
  .table {
    th, td {
      vertical-align: middle;
    }
  }
}

.modal {
  background-color: rgba(0, 0, 0, 0.5);
}

// Utility-Klassen
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 1;
}

// Styling für die inline Löschbestätigung
.delete-confirmation {
  background-color: #f8d7da;
  border-radius: 4px;
  padding: 0.25rem 0.5rem;
  animation: fadeIn 0.3s ease-in-out;
}

.confirmation-text {
  color: #721c24;
  font-size: 0.75rem;
  font-weight: 600;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 992px) {
  .stats-metrics {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 576px) {
  .stats-metrics {
    grid-template-columns: 1fr;
  }
}
</style> 